import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  InputAdornment,
  Alert,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useCreate, useNotification } from '@refinedev/core'
import { addMonths, startOfDay, endOfDay } from 'date-fns'
import { enNZ } from 'date-fns/locale'

interface CreateDiscountCodeDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

interface DiscountCodeFormData {
  code: string
  description: string
  discount_type: 'percentage'
  discount_value: number
  valid_from: Date | null
  valid_until: Date | null
  duration_months: number
  max_uses: number | null
}

export default function CreateDiscountCodeDialog({
  open,
  onClose,
  onSuccess,
}: CreateDiscountCodeDialogProps) {
  const { open: notify } = useNotification()
  const { mutate: createDiscountCode, isLoading } = useCreate()

  const [formData, setFormData] = useState<DiscountCodeFormData>({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 0,
    valid_from: new Date(),
    valid_until: addMonths(new Date(), 3),
    duration_months: 12,
    max_uses: null,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.code.trim()) {
      newErrors.code = 'Code is required'
    } else if (formData.code.length > 50) {
      newErrors.code = 'Code must be 50 characters or less'
    } else if (!/^[A-Za-z0-9-_]+$/.test(formData.code)) {
      newErrors.code =
        'Code can only contain letters, numbers, hyphens, and underscores'
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    }

    if (formData.discount_value <= 0) {
      newErrors.discount_value = 'Discount value must be greater than 0'
    }

    if (formData.discount_value > 100) {
      newErrors.discount_value = 'Percentage discount cannot exceed 100%'
    }

    if (!formData.valid_from) {
      newErrors.valid_from = 'Valid from date is required'
    }

    if (!formData.valid_until) {
      newErrors.valid_until = 'Valid until date is required'
    }

    if (
      formData.valid_from &&
      formData.valid_until &&
      formData.valid_from >= formData.valid_until
    ) {
      newErrors.valid_until = 'Valid until must be after valid from'
    }

    if (formData.duration_months <= 0) {
      newErrors.duration_months = 'Duration must be at least 1 month'
    }

    if (formData.max_uses !== null && formData.max_uses <= 0) {
      newErrors.max_uses =
        'Max uses must be greater than 0 or empty for unlimited'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    try {
      await createDiscountCode({
        resource: 'discount-codes',
        values: {
          ...formData,
          valid_from: formData.valid_from
            ? startOfDay(formData.valid_from).toISOString()
            : null,
          valid_until: formData.valid_until
            ? endOfDay(formData.valid_until).toISOString()
            : null,
          max_uses: formData.max_uses || null,
        },
      })
      onSuccess()
      handleClose()
    } catch (error: any) {
      notify?.({
        type: 'error',
        message: error.message || 'Failed to create discount code',
      })
    }
  }

  const handleClose = () => {
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      valid_from: new Date(),
      valid_until: addMonths(new Date(), 3),
      duration_months: 12,
      max_uses: null,
    })
    setErrors({})
    onClose()
  }

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let code = ''
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    setFormData({ ...formData, code })
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Discount Code</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enNZ}>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              {/* Code */}
              <Grid item xs={12}>
                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    label="Discount Code"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        code: e.target.value.toUpperCase(),
                      })
                    }
                    error={!!errors.code}
                    helperText={
                      errors.code ||
                      'Alphanumeric characters, hyphens, and underscores only'
                    }
                    placeholder="e.g., SAVE20, WELCOME-2024"
                  />
                  <Button
                    variant="outlined"
                    onClick={generateRandomCode}
                    sx={{ minWidth: 'auto', px: 2 }}
                  >
                    Generate
                  </Button>
                </Box>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  error={!!errors.description}
                  helperText={
                    errors.description ||
                    'Internal description for this discount code'
                  }
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Discount Value */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Discount Percentage"
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      discount_value: parseFloat(e.target.value) || 0,
                    })
                  }
                  error={!!errors.discount_value}
                  helperText={errors.discount_value}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Valid From and Until */}
              <Grid item xs={6}>
                <DatePicker
                  label="Valid From"
                  value={formData.valid_from}
                  onChange={(date) =>
                    setFormData({ ...formData, valid_from: date })
                  }
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.valid_from,
                      helperText: errors.valid_from,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <DatePicker
                  label="Valid Until"
                  value={formData.valid_until}
                  onChange={(date) =>
                    setFormData({ ...formData, valid_until: date })
                  }
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.valid_until,
                      helperText: errors.valid_until,
                    },
                  }}
                />
              </Grid>

              {/* Duration and Max Uses */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Discount Duration (months)"
                  type="number"
                  value={formData.duration_months}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      duration_months: parseInt(e.target.value) || 1,
                    })
                  }
                  error={!!errors.duration_months}
                  helperText={
                    errors.duration_months ||
                    'How long the discount applies to the subscription'
                  }
                  InputProps={{
                    inputProps: { min: 1 },
                  }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Max Uses (optional)"
                  type="number"
                  value={formData.max_uses || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      max_uses: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                  error={!!errors.max_uses}
                  helperText={
                    errors.max_uses || 'Leave empty for unlimited uses'
                  }
                  InputProps={{
                    inputProps: { min: 1 },
                  }}
                />
              </Grid>

              {/* Preview */}
              <Grid item xs={12}>
                <Alert severity="info">
                  <strong>Preview:</strong> Code "{formData.code || '[CODE]'}"
                  will give {formData.discount_value}% off for{' '}
                  {formData.duration_months} month
                  {formData.duration_months !== 1 ? 's' : ''}.{' '}
                  {formData.max_uses
                    ? `Limited to ${formData.max_uses} uses.`
                    : 'Unlimited uses.'}
                </Alert>
              </Grid>
            </Grid>
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isLoading}>
          {isLoading ? 'Creating...' : 'Create Discount Code'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
